/**
 * The feature registry — the authoritative list of what can be gated.
 *
 * Isomorphic and free of secrets: the browser needs the labels and the resolved values to
 * decide what to render, and the admin panel builds its whole configuration screen from
 * this object (`specs/04`, `specs/07`). Nothing here touches the database.
 *
 * **Code decides what features exist; the database decides who gets them.** A key with no
 * call site behind it is inert, so inventing one in the `plan_features` table must not be
 * possible — which is why `plan_features.feature_key` is plain text validated against this
 * registry rather than a foreign key to a `features` table (`specs/02`).
 *
 * `FeatureKey` being a union is the load-bearing part. `requireFeature('calc.revrse')`
 * fails to compile; without it that typo is a silently open gate, which is the worst
 * failure mode a paywall has.
 *
 * Adding a feature: add the key here, use it at a call site, give it a value in the seed
 * matrix. The admin UI picks it up with no admin-side code change.
 *
 * **Marketplaces are deliberately not in this registry.** They were, in the first draft of
 * `specs/04` — one `marketplace.<id>` key each — and they came out when `specs/09` made
 * marketplaces admin-creatable. Access to them is a resource entitlement resolved from the
 * `plan_marketplaces` join; see `Entitlements.marketplaces` and `canUseMarketplace` below.
 */

/** `boolean` features are on or off; `limit` features carry a count (`-1` = unlimited). */
export type FeatureType = 'boolean' | 'limit'

/**
 * How strongly a feature is actually enforced. Be honest here — a spec that overstates
 * enforcement is worse than none (`specs/04`).
 *
 * - `A` **hard**: the data or the computation lives on the server and the server checks
 *   entitlement before answering. Devtools do not get you in.
 * - `B` **write-enforced**: the client can be manipulated freely, but the server rejects
 *   the write. The user may see a fourth product row; nothing persists it.
 * - `C` **cosmetic**: UI only, trivially bypassed. The loader still resolves entitlements
 *   server-side, so an unentitled visitor's SSR'd HTML never contains the feature — but the
 *   code and the arithmetic ship in everyone's bundle, so a manipulated client gets it back.
 *
 * `calc.ads_simulation` and `calc.channel_comparison` are the Tier C features, and both are
 * deliberately so: there is no data to withhold. The ROAS↔ACOS arithmetic runs on numbers
 * the seller typed, against a formula Shopee publishes itself, and the price it adjusts is
 * already solved in the browser. The comparator ranks fee tables the browser already holds
 * — only the entitled ones, so an off-plan channel stays Tier A through `plan_marketplaces`.
 * Moving either server-side would buy a round trip per keystroke and no secrecy. Any
 * *new* feature that can only be Tier C should still move its computation to the server
 * instead — see `specs/04`.
 */
export type EnforcementTier = 'A' | 'B' | 'C'

export type FeatureDefinition = {
  type: FeatureType
  tier: EnforcementTier
  /** pt-BR, shown in the admin panel and on the paywall. */
  label: string
  /** One pt-BR sentence explaining what the seller gets. Paywall copy lives with the key. */
  pitch: string
}

export const FEATURES = {
  'calc.reverse': {
    type: 'boolean',
    tier: 'A',
    label: 'Cálculo reverso',
    pitch: 'Informe o preço que quer anunciar e veja a margem que sobra.',
  },
  'calc.price_insight': {
    type: 'boolean',
    tier: 'A',
    label: 'Sugestões inteligentes',
    pitch:
      'Encontre preços e embalagens mais eficientes ao cruzar uma faixa de tarifa.',
  },

  'calc.bundle_products': {
    type: 'limit',
    tier: 'B',
    label: 'Produtos por cálculo',
    pitch: 'Precifique kits com vários produtos em um único anúncio.',
  },
  'calc.coupons': {
    type: 'limit',
    tier: 'B',
    label: 'Cupons acumulados',
    pitch: 'Some vários cupons e promoções no mesmo cálculo.',
  },

  /**
   * Tier C, and the registry is where that is admitted rather than glossed. See the note on
   * `EnforcementTier` above: the gate is real in the rendered tree and fake in the bundle.
   */
  'calc.ads_simulation': {
    type: 'boolean',
    tier: 'C',
    label: 'Simulador de tráfego pago (Ads)',
    pitch:
      'Some o custo dos seus anúncios ao cálculo e veja quanto sobra de verdade.',
  },

  /**
   * Tier C for the same reason: the gate is real in the rendered tree and fake in the
   * bundle. The fee tables it compares are already in the payload for the calculator itself.
   */
  'calc.channel_comparison': {
    type: 'boolean',
    tier: 'C',
    label: 'Comparativo entre canais',
    pitch:
      'Veja quanto cobrar e quanto sobra em cada marketplace com os mesmos custos.',
  },

  'saved.calculations': {
    type: 'limit',
    tier: 'A',
    label: 'Cálculos salvos',
    pitch: 'Guarde cálculos com nome e volte a eles quando quiser.',
  },
  'saved.history': {
    type: 'boolean',
    tier: 'A',
    label: 'Histórico de cálculos',
    pitch: 'Acompanhe como seus preços mudaram ao longo do tempo.',
  },
} as const satisfies Record<string, FeatureDefinition>

export type FeatureKey = keyof typeof FEATURES

/** Keys whose value is a count. `limitOf` accepts only these, so misuse fails to compile. */
export type LimitFeatureKey = {
  [K in FeatureKey]: (typeof FEATURES)[K]['type'] extends 'limit' ? K : never
}[FeatureKey]

export const FEATURE_KEYS = Object.keys(FEATURES) as Array<FeatureKey>

/** `-1`, not NULL. NULL already means "no opinion"; overloading it grants silently (`specs/02`). */
export const UNLIMITED = -1

/** Narrows a string from the database, so an orphaned row cannot be treated as a feature. */
export function isFeatureKey(key: string): key is FeatureKey {
  return Object.prototype.hasOwnProperty.call(FEATURES, key)
}

/**
 * The feature key carried by a 403 from `requireFeature`, or `null` for any other failure.
 *
 * Duck-typed on purpose. The error is thrown as a `FeatureRequiredError` on the server, but
 * Start serializes it with seroval and the browser reconstructs a plain `Error` carrying the
 * same own properties — the class does not survive the wire, so `instanceof` would silently
 * never match and every paywall would render as a generic failure.
 *
 * It lives here rather than beside the error class because the client is its only caller and
 * `src/components/` may not import `src/server/` (`specs/01`).
 */
export function featureErrorKey(error: unknown): FeatureKey | null {
  if (!error || typeof error !== 'object') return null

  const { code, featureKey } = error as { code?: unknown; featureKey?: unknown }
  if (code !== 'FEATURE_REQUIRED' && code !== 'FEATURE_LIMIT') return null

  return typeof featureKey === 'string' && isFeatureKey(featureKey)
    ? featureKey
    : null
}

export type FeatureValue = boolean | number

/**
 * The resolved answer for one user, on one request.
 *
 * Safe to send to the browser — it is what the client renders from — and it never carries
 * the plan configuration, only the outcome.
 */
export type Entitlements = {
  planSlug: string
  planName: string
  isAdmin: boolean
  features: Record<FeatureKey, FeatureValue>
  /**
   * Marketplace ids this user may use, resolved from the `plan_marketplaces` join and not
   * from a feature key (`specs/09`).
   *
   * It is a list of row ids rather than a registry key per marketplace because
   * marketplaces are admin-creatable: a code key that has to be added by hand for every
   * new row puts back exactly the deploy that change removes. Enforcement does not weaken —
   * it is still `loadFeeTables(entitlements.marketplaces)`, and a marketplace outside this
   * list never has its fee data sent to the browser.
   */
  marketplaces: ReadonlyArray<string>
}

/**
 * `can` and `limitOf` live here rather than in `src/server/entitlements/resolve.ts` where
 * `specs/04` sketches them, for one reason: components must read them too, and
 * `src/components/` may not import `src/server/` (`specs/01`, enforced by ESLint). They are
 * record lookups over data the server already resolved, so they are pure by nature.
 * `resolve.ts` re-exports both, so the server-side surface the spec describes still holds.
 */
export function can(entitlements: Entitlements, key: FeatureKey): boolean {
  const value = entitlements.features[key]

  // For a limit feature "can" means "has any allowance at all" — an unlimited `-1` and a
  // positive count both qualify, a `0` does not.
  if (typeof value === 'number') return value !== 0
  return value === true
}

/** The configured count for a limit feature. `-1` means unlimited. */
export function limitOf(
  entitlements: Entitlements,
  key: LimitFeatureKey,
): number {
  const value = entitlements.features[key]
  return typeof value === 'number' ? value : 0
}

/**
 * Whether this user may use a marketplace.
 *
 * A plain membership test, and deliberately so: the resolver already decided, and the list
 * it produced is the only place the decision lives. It reads a row id, so an unknown or
 * misspelled id is simply absent — which is the fail-closed answer, not an error.
 */
export function canUseMarketplace(
  entitlements: Entitlements,
  marketplaceId: string,
): boolean {
  return entitlements.marketplaces.includes(marketplaceId)
}

/**
 * Whether one more item fits.
 *
 * Written as "would `count` items be allowed" rather than "is there room after `count`" so
 * that every caller — the client's advisory check and the server's authoritative one —
 * asks the same question and cannot be off by one in different directions.
 */
export function withinLimit(
  entitlements: Entitlements,
  key: LimitFeatureKey,
  count: number,
): boolean {
  const limit = limitOf(entitlements, key)
  return limit === UNLIMITED || count <= limit
}
